import React, { createContext, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loginRequest, signupRequest, logout, setPermissions } from '../redux/actions/authActions';
import { rbacAPI } from '../api/config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, isAuthenticated, loading, error, permissions } = useSelector(
    (state) => state.auth
  );

  /**
   * Re-hydrate permissions on every app mount (including page refresh).
   * Only runs when a token exists in localStorage but Redux permissions
   * are empty — which happens after a hard refresh or a new permission granted
   * without re-login.
   */
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;               // not logged in at all
    if (permissions && permissions.length > 0) return; // already loaded

    rbacAPI
      .getMyPermissions()
      .then((res) => {
        const perms = res.data?.permissions || res.data || [];
        if (Array.isArray(perms)) {
          dispatch(setPermissions(perms));
        }
      })
      .catch((err) => {
        // 401 → the interceptor in config.js will redirect to /login
        console.warn('Failed to re-hydrate permissions:', err?.response?.status);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (credentials) => {
    dispatch(loginRequest(credentials));
  };

  const signup = (userData) => {
    dispatch(signupRequest(userData));
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    permissions,
    login,
    signup,
    logout: handleLogout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;